/**
 * @fileoverview useTransactionOperations - Transaction CRUD operations.
 *
 * Orchestrates all transaction create, delete, and update operations by combining
 * form state, validation, and API calls. This is the "controller" layer of the
 * transaction management system.
 *
 * @description This hook provides:
 * - Add transaction with validation
 * - Delete transaction with confirmation
 * - Save edited transaction with validation
 * - Error handling for closed months
 * - User feedback via alerts/errors
 *
 * Operation flow:
 * 1. Validate form data
 * 2. Build transaction object
 * 3. Call API via monthlyData hook
 * 4. Handle success (reset form) or error (set error state)
 *
 * Month closed handling:
 * - Detects "closed month" errors from API
 * - Shows appropriate error messages
 * - Prevents operations on closed months
 *
 * @module hooks/useTransactionOperations
 */
import { validateTransaction, hasValidationErrors } from '../utils/validationUtils';
import { buildNewTransaction } from './helpers/addTransactionBuilder';
import { ConflictError } from '../services/api';

/**
 * Hook for transaction CRUD operations.
 *
 * @param {Object} monthlyData - Monthly data hook with CRUD methods
 * @param {Array<string>} userCategories - User category names
 * @param {Object} formState - Add form state hook
 * @param {Object} editState - Edit form state hook
 * @param {Object} validationState - Validation error state hook
 * @param {boolean} singleBankMode - Whether budget is in single bank mode (optional)
 * @param {Array<Object>} users - Array of user objects with userKey and userName
 * @returns {Object} Transaction operations
 * @returns {Function} return.addTransaction - Add new transaction
 * @returns {Function} return.deleteTransaction - Delete transaction with confirmation
 * @returns {Function} return.saveEdit - Save edited transaction
 *
 * @example
 * const ops = useTransactionOperations(monthlyData, userCategories, form, edit, validation, false, users);
 * const success = await ops.addTransaction(); // Validates and adds
 * await ops.deleteTransaction(transactionId); // Confirms and deletes
 */
export const useTransactionOperations = (
    monthlyData,
    userCategories,
    formState,
    editState,
    validationState,
    singleBankMode = false,
    users = []
) => {
    // Add new transaction
    const addTransaction = async () => {
        const {
            amount,
            description,
            transactionCategories,
            date,
            transactionType,
            currentUserKey,
            adjustmentFromCategory,
            adjustmentToCategory,
            resetForm
        } = formState;

        const { setErrors } = validationState;

        const validationErrors = validateTransaction({
            amount,
            description,
            transactionCategories: transactionType === 'Adjustment' ? [] : transactionCategories,
            date,
            transactionType,
            currentUserKey,
            adjustmentFromCategory,
            adjustmentToCategory
        }, singleBankMode);

        if (hasValidationErrors(validationErrors)) {
            setErrors(validationErrors);
            return false;
        }

        setErrors({});

        const newTransaction = buildNewTransaction(formState, userCategories, users);

        // Use monthly data system to add transaction
        try {
            await monthlyData.addTransactionToMonth(newTransaction);
            resetForm();
            return true;
        } catch (error) {
            // Error handling
            if (error.message && error.message.includes('closed')) {
                setErrors({ general: 'This month is closed. Please open the month before adding transactions.' });
            } else {
                setErrors({ general: 'Failed to add transaction. Please try again.' });
            }
            return false;
        }
    };

    // Delete transaction
    const deleteTransaction = async (transactionId) => {
        const transactionToDelete = monthlyData.activeTransactions.find(t => t.id === transactionId);

        let categoryInfo = '';
        if (transactionToDelete.type === 'Adjustment') {
            categoryInfo = `${transactionToDelete.adjustmentFromCategory} → ${transactionToDelete.adjustmentToCategory}`;
        } else {
            categoryInfo = transactionToDelete.categories
                .map(cat => `${cat.category}: ${cat.amount >= 0 ? '+' : '-'}$${Math.abs(cat.amount).toFixed(2)}`)
                .join(', ');
        }

        const confirmMessage = `Are you sure you want to delete this transaction?\n\n` +
            `Amount: ${transactionToDelete.amount.toFixed(2)}\n` +
            `Description: ${transactionToDelete.description}\n` +
            `Categories: ${categoryInfo}\n` +
            `Date: ${new Date(transactionToDelete.date).toLocaleDateString()}`;

        if (window.confirm(confirmMessage)) {
            try {
                await monthlyData.deleteTransactionFromMonth(transactionId);
                return true;
            } catch (error) {
                // Error handling
                const errorMsg = error.message || error.toString();
                if (errorMsg.includes('closed')) {
                    alert('Cannot delete transactions from a closed month. Please open the month first.');
                } else {
                    alert('Failed to delete transaction: ' + errorMsg);
                }
                return false;
            }
        }
        return false;
    };

    /**
     * Save edited transaction with conflict detection.
     * If a version conflict occurs, throws ConflictError for the caller to handle.
     *
     * @returns {Promise<boolean|Object>} True on success, false on validation error
     * @throws {ConflictError} When transaction was modified by another user
     */
    const saveEdit = async () => {
        const { buildUpdatedTransaction, validateEdit, cancelEdit } = editState;
        const { setEditErrors } = validationState;

        if (!validateEdit()) {
            return false;
        }

        const updatedTransaction = buildUpdatedTransaction();

        // Use monthly data system to update transaction
        try {
            await monthlyData.updateTransactionInMonth(updatedTransaction);
            cancelEdit();
            return true;
        } catch (error) {
            // Re-throw ConflictError for caller to handle with conflict resolution UI
            if (error instanceof ConflictError || error.isConflict) {
                throw error;
            }

            // Handle other errors
            const errorMsg = error.message || error.toString();
            if (errorMsg.includes('closed')) {
                setEditErrors({
                    general: 'Cannot edit transactions in a closed month. Please open the month first.'
                });
            } else {
                setEditErrors({ general: errorMsg || 'Failed to update transaction. Please try again.' });
            }
            return false;
        }
    };

    return {
        addTransaction,
        deleteTransaction,
        saveEdit
    };
};
